import { Button, makeStyles, tokens } from '@fluentui/react-components';
import { AddRegular } from "@fluentui/react-icons";
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';

import { AppDetailsHeader } from '@tdp/common/components/AppDetailsHeader';
import { makeDocsLink } from '@tdp/common/constants';

import { AppFeaturesDataGrid } from './AppFeaturesDataGrid';
import { AppFeaturesDialog } from './AppFeaturesDialog';
import { IAppFeaturesDataGrid } from './IAppFeaturesDataGrid';
import { createAppZoomHook } from '../../../hooks';
import { appFeaturesCommonStyles } from '../AppFeaturesCommon/appFeaturesCommon.styles';
import { AppFeaturesEmpty } from '../AppFeaturesCommon/AppFeaturesEmpty';
import { createAppFeaturesFooter } from '../AppFeaturesCommon/AppFeaturesFooter';




const useStaticTabs = createAppZoomHook(app => app.staticTabs);
const Footer = createAppFeaturesFooter(app => [app.staticTabs]);


const useStyles = makeStyles({
  gridContainer: {
    padding: tokens.spacingHorizontalL,
    marginTop: tokens.spacingVerticalL,
  },
  button: {
    marginTop: tokens.spacingVerticalXL,
  }
})

export const AppFeaturesPersonalTab: React.FC = () => {
  const navigate = useNavigate();
  const baseStyles = appFeaturesCommonStyles();
  const classes = useStyles();
  const intl = useIntl();

  const appId = useParams<{ appId: string }>().appId ?? '';
  const tabList = useStaticTabs(appId);

  const [isNewTabDialogOpen, setIsNewTabDialogOpen] = React.useState(false);
  const [activeItem, setActiveItem] = React.useState<IAppFeaturesDataGrid | null>(null);
  const isEmpty = !tabList.current || tabList.current.length === 0;

  const onMoveUp = (index: number) => {
    const current = tabList.current;
    if (current && index > 0) {
      const newList = [...current];
      const temp = newList[index - 1];
      newList[index - 1] = newList[index];
      newList[index] = temp;
      tabList.update(() => newList);
    }
  }

  const onMoveDown = (index: number) => {
    const current = tabList.current;
    if (current && index < current.length - 1) {
      const newList = [...current];
      const temp = newList[index + 1];
      newList[index + 1] = newList[index];
      newList[index] = temp;
      tabList.update(() => newList);
    }
  }

  const onDelete = (index: number) => {
    const current = tabList.current;
    if (current) {
      const newList = [...current];
      newList.splice(index, 1);
      tabList.update(() => newList);
    }
  }

  const onAddOrEdit = (item: IAppFeaturesDataGrid) => {
    const current = tabList.current;
    if (current) {
      const newList = [...current];
      if (item.index === undefined || item.index < 0 || item.index >= newList.length) {
        newList.push(item);
      } else {
        newList[item.index] = item;
      }
      tabList.update(() => newList);
    }
  }

  const onEditDailogOpen = (index: number) => {
    const current = [...(tabList.current ?? [])];
    if (index < current.length && index >= 0) {
      const item = current[index];
      setActiveItem({
        name: item.name ?? '',
        contentUrl: item.websiteUrl ?? '',
        objectId: item.objectId ?? '',
        websiteUrl: item.websiteUrl ?? '',
        index: index,
        scopes: [...(item.scopes ?? [])],
        context: [...(item.context ?? [])],
      });
    }
    else {
      setActiveItem({
        name: '',
        contentUrl: '',
        objectId: '',
        websiteUrl: '',
        index: index,
        scopes: [],
        context: []
      });
    }
    setIsNewTabDialogOpen(true);
  }

  const Empty = () => <AppFeaturesEmpty
    onClick={() => { onEditDailogOpen(-1); }}
    buttonText={intl.formatMessage({
      defaultMessage: 'Create your first personal app tab',
      description: 'Button text to add a personal app'
    })}
  />

  return (
    <div className={baseStyles.wrapper}>
      {isNewTabDialogOpen && <AppFeaturesDialog
        open={isNewTabDialogOpen}
        setOpen={setIsNewTabDialogOpen}
        activeItem={activeItem!}
        onAddOrEdit={onAddOrEdit}
      />}
      <div className={baseStyles.container}>
        <AppDetailsHeader
          onBackNavigation={() => { navigate(-1); }}
          showBackNavigation
          title={<FormattedMessage
            defaultMessage="Personal app"
            description={'Title for Personal app Section'}
          />}
          description={<FormattedMessage
            defaultMessage="A dedicated space or bot to help individual users focus on their own tasks or view activities important to them."
            description={'Description for Personal app Section'}
          />}
          link={<FormattedMessage
            defaultMessage="Learn more about Personal app"
            description={'Link to learn more about Personal app Section'}
          />}
          href={makeDocsLink("microsoftteams/platform/tabs/how-to/create-personal-tab")}
        />
        {isEmpty ? <Empty /> :
          <div className={classes.gridContainer}>
            <AppFeaturesDataGrid items={tabList.current?.map((item, index) => ({
              name: item.name ?? item.entityId ?? '',
              contentUrl: item.websiteUrl ?? '',
              scopes: [...(item.scopes ?? [])],
              objectId: item.objectId ?? '',
              index: index,
            })) ?? [] as IAppFeaturesDataGrid[]}
              onMoveUp={onMoveUp}
              onMoveDown={onMoveDown}
              onDelete={onDelete}
              onEditDailogOpen={onEditDailogOpen}
            />
            <Button
              className={classes.button}
              appearance='subtle'
              icon={<AddRegular />}
              onClick={() => { onEditDailogOpen(-1); }}
            >
              <FormattedMessage
                defaultMessage="Add a personal app tab"
                description={'Button text to add a personal app'}
              />
            </Button>
          </div>
        }
      </div>
      <Footer />
    </div>
  );
}
